<script setup>
import { usePrivateChannel } from '@/composables/usePrivateChannel'
import { useTenantId } from '@/composables/useTenantId'
import { useKdsStore } from '@/stores/kds'
import { useStationStore } from '@/stores/station'

definePage({
  meta: {
    layout: 'blank',
    action: 'read',
    subject: 'Station',
  },
})

const route = useRoute()
const router = useRouter()
const kds = useKdsStore()
const stationStore = useStationStore()
const tenantId = useTenantId()

// Validasi stationId
const stationId = computed(() => {
  const id = route.params.stationId
  const numId = Number(id)
  return Number.isNaN(numId) ? null : numId
})

const isLoading = ref(true)
const loadError = ref('')

const load = async () => {
  if (!stationId.value) {
    loadError.value = 'Station ID tidak valid.'
    isLoading.value = false
    setTimeout(() => router.push({ name: 'kds' }), 2000)
    return
  }

  isLoading.value = true
  loadError.value = ''
  try {
    if (!stationStore.stations.length)
      await stationStore.fetchStations()

    await kds.fetchTickets(stationId.value)
  }
  catch (err) {
    loadError.value = err?.data?.message || err?.message || 'Gagal memuat tiket dapur.'
  }
  finally {
    isLoading.value = false
  }
}

onMounted(load)
watch(stationId, (newId) => { if (newId) load() })

// ─ Realtime (Echo) ───────────────────────────────────────────────────────
const channelName = computed(() => {
  if (!tenantId.value || !stationId.value) return null
  return `tenant.${tenantId.value}.station.${stationId.value}`
})

const { connectionState } = channelName.value 
  ? usePrivateChannel(channelName, {
      NewKitchenTicket: payload => kds.addTicketFromRealtime(payload),
      StationTicketUpdated: payload => kds.applyTicketUpdate(payload),
    })
  : { connectionState: ref('disconnected') }

const connectionColor = computed(() => {
  if (connectionState.value === 'connected') return 'success'
  if (connectionState.value === 'connecting') return 'warning'
  return 'error'
})

// Fallback polling
let fallbackPollTimer = null
onMounted(() => {
  fallbackPollTimer = setInterval(() => {
    if (connectionState.value !== 'connected' && stationId.value)
      kds.fetchTickets(stationId.value)
  }, 30000)
})
onBeforeUnmount(() => clearInterval(fallbackPollTimer))

// ── Elapsed timer ─────────────────────────────────────────────────────────
const now = ref(Date.now())
let clockTimer = null
onMounted(() => { clockTimer = setInterval(() => { now.value = Date.now() }, 1000) })
onBeforeUnmount(() => clearInterval(clockTimer))

const elapsedMinutes = createdAt => Math.floor((now.value - new Date(createdAt).getTime()) / 60000)

const elapsedLabel = createdAt => {
  const mins = elapsedMinutes(createdAt)
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return h > 0 ? `${h}j ${m}m` : `${m}m`
}

const elapsedColor = createdAt => {
  const mins = elapsedMinutes(createdAt)
  if (mins >= 10) return 'error'
  if (mins >= 5) return 'warning'
  return 'success'
}

// ── Actions ───────────────────────────────────────────────────────────────
const bumpLabel = {
  queued: '👨‍ Mulai Proses',
  preparing: '✅ Tandai Siap',
  ready: '🏃 Selesai / Antar',
}

const isBumping = ref({})

const bump = async ticket => {
  isBumping.value[ticket.id] = true
  try {
    await kds.bumpTicket(ticket)
  }
  finally {
    isBumping.value[ticket.id] = false
  }
}

const toggleItem = async (item, ticket) => {
  const nextItemStatus = item.status === 'served' ? ticket.status : 'served'
  await kds.bumpTicketItem(item.id, nextItemStatus)
}

// ── Station switcher & fullscreen ───────────────────────────────────────
const switchStation = id => {
  router.push({ name: 'kds-station-id', params: { stationId: id } })
}

const isFullscreen = ref(false)
const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen()
    isFullscreen.value = true
  }
  else {
    document.exitFullscreen()
    isFullscreen.value = false
  }
}

const columns = [
  { key: 'queued', title: 'ANTREAN MASUK', icon: 'ri-time-line', color: 'warning', bg: '#FFF8E1' },
  { key: 'preparing', title: 'SEDANG DIPROSES', icon: 'ri-fire-line', color: 'info', bg: '#E3F2FD' },
  { key: 'ready', title: 'SIAP DIANTAR', icon: 'ri-check-double-line', color: 'success', bg: '#E8F5E9' },
]
</script>

<template>
  <div class="kds-screen">
    <!-- Top Bar -->
    <VAppBar flat color="surface" density="comfortable" class="kds-header border-b">
      <VAppBarTitle class="d-flex align-center gap-3">
        <VAvatar color="primary" variant="tonal" size="40">
          <VIcon icon="ri-tv-2-line" size="22" />
        </VAvatar>
        <div>
          <div class="text-h6 font-weight-bold line-height-1">
            KDS — {{ stationStore.stations.find(s => s.id === stationId)?.name || 'Stasiun' }}
          </div>
          <div class="d-flex align-center gap-2 mt-1">
            <VChip size="x-small" :color="connectionColor" variant="flat">
              <VIcon icon="ri-wifi-line" size="12" class="mr-1" />
              {{ connectionState.toUpperCase() }}
            </VChip>
          </div>
        </div>
      </VAppBarTitle>

      <template #append>
        <!-- Large Clock -->
        <div class="text-h5 font-weight-bold text-primary mr-6 font-monospace">
          {{ new Date(now).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }}
        </div>

        <!-- Station Switcher -->
        <div class="d-flex gap-1 mr-4">
          <VBtn
            v-for="station in stationStore.stations"
            :key="station.id"
            size="small"
            :variant="station.id === stationId ? 'flat' : 'tonal'"
            :color="station.id === stationId ? 'primary' : 'default'"
            @click="switchStation(station.id)"
          >
            {{ station.name }}
          </VBtn>
        </div>

        <!-- Fullscreen -->
        <VBtn icon variant="text" @click="toggleFullscreen">
          <VIcon :icon="isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'" size="24" />
        </VBtn>
      </template>
    </VAppBar>

    <!-- Main Board -->
    <VMain class="kds-board-container pa-4">
      <VAlert v-if="loadError" type="error" variant="tonal" class="mb-4">
        {{ loadError }}
        <template #append>
          <VBtn size="small" variant="text" @click="load">Coba Lagi</VBtn>
        </template>
      </VAlert>

      <div v-else class="kds-grid">
        <div v-for="column in columns" :key="column.key" class="kds-column">
          <!-- Column Header -->
          <div class="kds-column-header pa-3 rounded-t-lg" :style="{ backgroundColor: column.bg }">
            <div class="d-flex align-center justify-space-between">
              <div class="d-flex align-center gap-2">
                <VIcon :icon="column.icon" :color="column.color" size="24" />
                <span class="text-subtitle-1 font-weight-bold" :class="`text-${column.color}`">
                  {{ column.title }}
                </span>
              </div>
              <VBadge :content="kds.ticketsByStatus[column.key].length" :color="column.color" inline />
            </div>
          </div>

          <!-- Column Body -->
          <div class="kds-column-body pa-2">
            <VSkeletonLoader v-if="isLoading" type="card" class="mb-3" />
            
            <div v-else-if="!kds.ticketsByStatus[column.key].length" class="kds-empty-state">
              <VIcon icon="ri-inbox-line" size="48" color="grey-lighten-1" />
              <p class="text-body-2 text-medium-emphasis mt-2 mb-0">Tidak ada tiket</p>
            </div>

            <VCard
              v-for="ticket in kds.ticketsByStatus[column.key]"
              :key="ticket.id"
              variant="outlined"
              class="kds-ticket-card mb-3"
              :class="{ 'border-start-thick': true, [`border-s-${column.color}`]: true }"
            >
              <VCardText class="pa-3">
                <!-- Ticket Header -->
                <div class="d-flex justify-space-between align-start mb-3">
                  <div>
                    <div class="text-h4 font-weight-black text-primary line-height-1 mb-1">
                      {{ ticket.ticket_number }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      <VIcon icon="ri-table-line" size="14" class="mr-1" />
                      Meja <strong>{{ ticket.table_code || '-' }}</strong>
                      <span class="mx-1">·</span>
                      <VIcon icon="ri-hashtag" size="14" class="mr-1" />
                      {{ ticket.order_number }}
                    </div>
                  </div>
                  <VChip 
                    :color="elapsedColor(ticket.created_at)" 
                    variant="flat" 
                    size="large"
                    class="font-weight-bold"
                  >
                    <VIcon icon="ri-timer-line" size="16" class="mr-1" />
                    {{ elapsedLabel(ticket.created_at) }}
                  </VChip>
                </div>

                <VDivider class="mb-3" />

                <!-- Items List -->
                <div class="kds-items-list">
                  <div 
                    v-for="item in ticket.items" 
                    :key="item.id" 
                    class="kds-item-row"
                    :class="{ 'item-served': item.status === 'served' }"
                    @click="toggleItem(item, ticket)"
                  >
                    <div class="kds-item-checkbox">
                      <VIcon 
                        :icon="item.status === 'served' ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'" 
                        :color="item.status === 'served' ? 'success' : 'grey-lighten-1'"
                        size="24"
                      />
                    </div>
                    <div class="kds-item-content flex-grow-1">
                      <div class="text-body-1 font-weight-medium">
                        <span class="text-h6 font-weight-bold mr-1">{{ item.quantity }}x</span>
                        {{ item.product_name }}
                      </div>
                      <div v-if="item.variant_name" class="text-caption text-medium-emphasis">
                        {{ item.variant_name }}
                      </div>
                      <div v-if="item.notes" class="text-caption text-error font-weight-bold mt-1">
                        ⚠️ {{ item.notes }}
                      </div>
                    </div>
                  </div>
                </div>
              </VCardText>

              <!-- Action Button -->
              <VCardActions v-if="bumpLabel[ticket.status]" class="pa-3 pt-0">
                <VBtn 
                  block 
                  size="large" 
                  :color="column.color" 
                  variant="flat"
                  :loading="isBumping[ticket.id]"
                  @click="bump(ticket)"
                >
                  {{ bumpLabel[ticket.status] }}
                </VBtn>
              </VCardActions>
            </VCard>
          </div>
        </div>
      </div>
    </VMain>
  </div>
</template>

<style scoped>
.kds-screen {
  min-block-size: 100vh;
  background: #F4F5F7;
  display: flex;
  flex-direction: column;
  /* Papan KDS ini kiosk display dengan background TERANG yang di-hardcode di
     atas (bukan ikut tema app) — tapi teks di dalamnya (nama item, dll) pakai
     class utility Vuetify polos (text-body-1, text-caption, dst) yang warnanya
     ambil dari --v-theme-on-surface milik tema AKTIF. Kalau tema app sedang
     dark, on-surface jadi putih -> teks putih di atas kartu putih, tak
     terbaca. Override token tema ini lokal di sini supaya SEMUA teks turunan
     tetap gelap apapun tema global-nya, tanpa perlu !important di tiap class. */
  --v-theme-on-surface: 26, 26, 26;
}

.kds-header {
  border-block-end: 1px solid rgba(0, 0, 0, 0.08);
}

.font-monospace {
  font-family: 'Courier New', Courier, monospace;
  letter-spacing: 2px;
}

.kds-board-container {
  flex: 1;
  overflow: hidden;
}

.kds-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  height: 100%;
  align-items: start;
}

.kds-column {
  display: flex;
  flex-direction: column;
  max-block-size: calc(100vh - 100px);
  background: #FFFFFF;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

/* Header kolom dengan warna teks yang kontras */
.kds-column-header {
  border-block-end: 2px solid rgba(0, 0, 0, 0.05);
  min-height:100px;
}

/* Judul kolom - warna gelap agar kontras dengan background terang */
.kds-column-header .text-subtitle-1 {
  color: #1a1a1a !important;
  font-weight: 700 !important;
  font-size: 1.1rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Icon di header kolom - warna gelap */
.kds-column-header .v-icon {
  color: #1a1a1a !important;
}

/* Badge jumlah tiket - lebih prominent */
.kds-column-header .v-badge {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 4px 8px;
}

.kds-column-header .v-badge .v-chip {
  font-weight: 700 !important;
  font-size: 0.9rem !important;
}

.kds-column-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

/* Custom Scrollbar for Kitchen Display */
.kds-column-body::-webkit-scrollbar {
  width: 8px;
}
.kds-column-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.kds-column-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.kds-column-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.kds-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  text-align: center;
  opacity: 0.6;
}

.kds-ticket-card {
  transition: transform 0.2s, box-shadow 0.2s;
  border-radius: 8px;
}

.kds-ticket-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.border-start-thick {
  border-inline-start-width: 6px !important;
}

.kds-item-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
  margin-bottom: 4px;
}

.kds-item-row:hover {
  background: #F8FAFC;
}

.item-served {
  opacity: 0.5;
}

.item-served .kds-item-content {
  text-decoration: line-through;
}

.kds-item-checkbox {
  display: flex;
  align-items: center;
  padding-top: 2px;
}

@media (max-width: 1200px) {
  .kds-grid {
    grid-template-columns: 1fr;
  }
  .kds-column {
    max-block-size: none;
  }
}
</style>
